import { useMemo } from 'react';

import { Button, Divider, Stack, styled } from '@mui/material';

import { AppModal, BaseModalProps } from '@/components';
import { useCFSContext } from '@/hooks';
import { AssignedUnitShiftStatus, IAgency } from '@/models';

const CFSAddressChangeModalRoot = styled(AppModal)(({ theme }) => ({
  '& .modal-content': {
    position: 'absolute',
    minWidth: 500,
    padding: 20,
    borderRadius: 8,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 600,
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: 600,
    },

    '& .btn-close': {
      position: 'absolute',
      right: 8,
      top: 8,
      color: theme.palette.error.main,
    },
    '& .separator': {
      marginTop: 20,
      marginBottom: 20,
    },
  },
}));

interface CFSAddressChangeModalProps extends Omit<BaseModalProps, 'onClose'> {
  onSubmit: () => void;
  open: boolean;
  isRemovingAddress?: boolean;
  onClose: () => void;
  removedAgencies: IAgency[];
}

export const CFSAddressChangeModal = ({
  removedAgencies,
  onClose,
  open,
  isRemovingAddress,
  onSubmit,
}: CFSAddressChangeModalProps) => {
  const { cfs } = useCFSContext();

  const removedAgenciesIds = removedAgencies.map((agc) => agc._id);

  const units = useMemo(() => {
    return (
      cfs?.assignedUnitShifts
        ?.filter(
          (au) =>
            removedAgenciesIds.includes(au?.unitShift?.unit?.agency) &&
            au.status !== AssignedUnitShiftStatus.CANCELED &&
            au.status !== AssignedUnitShiftStatus.COMPLETED,
        )
        .map((au) => au?.unitShift?.unit?.name) || []
    );
  }, [cfs, removedAgenciesIds]);

  const agencyName = removedAgencies.map((agc) => agc.name).join(' ');

  return (
    <CFSAddressChangeModalRoot
      onClose={onClose}
      open={open}
      title="Jurisdiction Warning!"
      hideCloseButton
    >
      <Stack rowGap={2} divider={<Divider />}>
        <Stack>
          {isRemovingAddress ? (
            <Stack>
              You&apos;ve entered an address that, due to jurisdiction
              restrictions, will unassign the agency {agencyName} from the CFS.
              Assignments to the following units will be cancelled:
            </Stack>
          ) : (
            <Stack>
              Removing an address will unassign the agency {agencyName} from the
              CFS. Assignments for the following units will be canceled:
            </Stack>
          )}

          {units?.length ? (
            <Stack>
              {units?.map((u) => (
                <Stack key={u} sx={{ px: 2 }}>
                  • {u}
                </Stack>
              ))}
            </Stack>
          ) : (
            <></>
          )}
        </Stack>

        <Stack flexDirection="row" justifyContent="space-between">
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={onSubmit}>
            Unassign
          </Button>
        </Stack>
      </Stack>
    </CFSAddressChangeModalRoot>
  );
};
